import { Link } from "@remix-run/react";
import { useEffect, useId, useRef, useState } from "react";

import { Switch } from "~/components/ui/switch";
import { cn } from "~/lib/utils";

import type { ConsentCategories } from "~/lib/tracking/consent";

/**
 * Aviso de cookies propio (sin CMP de terceros: cero peso extra y cero dependencia).
 *
 * - Se monta SOLO en el cliente, después de hidratar y en idle: no toca el HTML del SSR que leen
 *   los crawlers ni compite por el LCP.
 * - "Aceptar", "Rechazar" y "Elegir" tienen EXACTAMENTE el mismo peso visual: rechazar tiene que
 *   ser tan fácil como aceptar.
 * - No bloquea la página (no es modal): se puede seguir navegando sin elegir, y mientras tanto no
 *   se carga ninguna plataforma.
 * - En mobile convive con las barras fijas inferiores (`[data-bottom-bar]`, el CTA de la ficha):
 *   se apoya encima, y le suma al `body` el alto del aviso para que nada quede tapado sin poder
 *   scrollearlo (el botón de /inscripcion, por ejemplo).
 */

export type ConsentVendorNames = { analytics: string[]; ads: string[] };

type Props = {
  mode: "summary" | "preferences";
  initial: ConsentCategories;
  vendors: ConsentVendorNames;
  /** Reabierto desde el footer: se puede cerrar sin cambiar nada. */
  dismissible: boolean;
  onSave: (choice: ConsentCategories) => void;
  onModeChange: (mode: "summary" | "preferences") => void;
  onDismiss: () => void;
};

const BUTTON =
  "inline-flex min-h-11 flex-1 items-center justify-center rounded-[8px] border border-landing-text/70 bg-transparent px-4 text-[15px] font-semibold text-landing-text transition-colors duration-100 hover:bg-landing-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-landing-text focus-visible:ring-offset-2 focus-visible:ring-offset-landing-white";

function useBottomBarOffset() {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const measure = () => {
      const bar = document.querySelector<HTMLElement>("[data-bottom-bar]");
      setOffset(bar ? Math.round(bar.getBoundingClientRect().height) : 0);
    };
    measure();
    window.addEventListener("resize", measure);
    const timer = window.setInterval(measure, 1500);
    return () => {
      window.removeEventListener("resize", measure);
      window.clearInterval(timer);
    };
  }, []);
  return offset;
}

export function ConsentBanner({
  mode,
  initial,
  vendors,
  dismissible,
  onSave,
  onModeChange,
  onDismiss,
}: Props) {
  const [choice, setChoice] = useState<ConsentCategories>(initial);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const bottomBar = useBottomBarOffset();

  useEffect(() => setChoice(initial), [initial]);

  // Reservar el alto del aviso al final de la página mientras está abierto. Es un espaciador en
  // el flujo y no un padding en <body>: el body tiene `height: 100%` y el contenido lo desborda,
  // así que un padding ahí no agrega scroll.
  const [reserved, setReserved] = useState(0);
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const sync = () => setReserved(Math.round(panel.getBoundingClientRect().height) + 24);
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(panel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (mode === "preferences") panelRef.current?.querySelector<HTMLElement>("button")?.focus();
  }, [mode]);

  useEffect(() => {
    if (!dismissible) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [dismissible, onDismiss]);

  const describe = (names: string[], fallback: string) =>
    names.length ? names.join(", ") : fallback;

  return (
    <>
      <div aria-hidden style={{ height: reserved + bottomBar }} data-testid="consent-spacer" />
      <div
        ref={panelRef}
        role="region"
        aria-labelledby={titleId}
        data-testid="consent-banner"
        data-fab-ignore
        style={{ bottom: `calc(${bottomBar}px + 12px + env(safe-area-inset-bottom, 0px))` }}
        className="fixed inset-x-3 z-[60] max-h-[calc(100dvh-24px)] overflow-y-auto rounded-xl border border-landing-border bg-landing-white p-4 font-landing sm:p-5 text-landing-text shadow-landing-lg sm:inset-x-auto sm:right-6 sm:w-[420px]"
      >
        <h2
          id={titleId}
          className="font-display text-[18px] font-semibold leading-tight sm:text-[20px]"
        >
          {mode === "summary" ? "Cookies y medición" : "Elegí qué medimos"}
        </h2>

        {mode === "summary" ? (
          <p className="mt-1.5 text-[13.5px] leading-snug text-landing-muted sm:mt-2 sm:text-[14px] sm:leading-relaxed">
            Usamos cookies propias para que el sitio funcione y para saber de qué campaña llegaste.
            Con tu permiso, también medimos el uso del sitio y los anuncios con herramientas de
            otras empresas. Lo cambiás cuando quieras desde el pie de página.{" "}
            <Link to="/privacy#cookies" className="font-semibold text-landing-text underline">
              Política de privacidad
            </Link>
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-landing-border">
            <li className="flex items-start justify-between gap-4 py-3">
              <div>
                <p className="text-[15px] font-semibold">Necesarias</p>
                <p className="mt-0.5 text-[13px] leading-relaxed text-landing-muted">
                  Tu sesión, esta elección y desde qué campaña llegaste (eso queda en AcademIA).
                </p>
              </div>
              <span className="shrink-0 pt-0.5 text-[13px] font-semibold text-landing-muted">
                Siempre
              </span>
            </li>
            <li className="flex items-start justify-between gap-4 py-3">
              <label htmlFor={`${titleId}-analytics`} className="cursor-pointer">
                <span className="block text-[15px] font-semibold">Analítica</span>
                <span className="mt-0.5 block text-[13px] leading-relaxed text-landing-muted">
                  Qué páginas se usan y dónde se traba la gente, para mejorar el sitio.{" "}
                  {describe(vendors.analytics, "Hoy no hay ninguna herramienta activa.")}
                </span>
              </label>
              <Switch
                id={`${titleId}-analytics`}
                checked={choice.analytics}
                onCheckedChange={(analytics) => setChoice((prev) => ({ ...prev, analytics }))}
                data-testid="consent-analytics"
                className="mt-0.5 data-[state=checked]:bg-landing-text data-[state=unchecked]:bg-landing-stone-300"
              />
            </li>
            <li className="flex items-start justify-between gap-4 py-3">
              <label htmlFor={`${titleId}-ads`} className="cursor-pointer">
                <span className="block text-[15px] font-semibold">Publicidad</span>
                <span className="mt-0.5 block text-[13px] leading-relaxed text-landing-muted">
                  Qué anuncio te trajo, para no gastar en publicidad que no sirve. Si la aceptás,
                  también les avisamos cuando te registrás o te inscribís, con tu teléfono cifrado.{" "}
                  {describe(vendors.ads, "Hoy no hay ninguna plataforma activa.")}
                </span>
              </label>
              <Switch
                id={`${titleId}-ads`}
                checked={choice.ads}
                onCheckedChange={(ads) => setChoice((prev) => ({ ...prev, ads }))}
                data-testid="consent-ads"
                className="mt-0.5 data-[state=checked]:bg-landing-text data-[state=unchecked]:bg-landing-stone-300"
              />
            </li>
          </ul>
        )}

        <div
          className={cn(
            "mt-3 flex gap-2 sm:mt-4",
            mode === "preferences" && "flex-col sm:flex-row",
          )}
        >
          {mode === "summary" ? (
            <>
              <button
                type="button"
                className={BUTTON}
                onClick={() => onSave({ analytics: false, ads: false })}
                data-testid="consent-reject"
              >
                Rechazar
              </button>
              <button
                type="button"
                className={BUTTON}
                onClick={() => onModeChange("preferences")}
                data-testid="consent-choose"
              >
                Elegir
              </button>
              <button
                type="button"
                className={BUTTON}
                onClick={() => onSave({ analytics: true, ads: true })}
                data-testid="consent-accept"
              >
                Aceptar
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className={BUTTON}
                onClick={() => onSave({ analytics: false, ads: false })}
                data-testid="consent-reject"
              >
                Rechazar todo
              </button>
              <button
                type="button"
                className={BUTTON}
                onClick={() => onSave(choice)}
                data-testid="consent-save"
              >
                Guardar elección
              </button>
            </>
          )}
        </div>

        {dismissible && (
          <button
            type="button"
            onClick={onDismiss}
            className="mt-3 w-full text-center text-[13px] font-semibold text-landing-muted underline"
          >
            Cerrar sin cambiar nada
          </button>
        )}
      </div>
    </>
  );
}
